import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/api/userService';
import type { McpKeyMetadata } from '../../services/api/types';
import './McpKeyModal.css';

/**
 * Scope definitions with the MCP tools each scope grants access to.
 * Only scopes with active tools are shown. When new tools are added
 * to the MCP server, add them here so admins can see what they're granting.
 */
interface ScopeDefinition {
  scope: string;
  labelKey: string;
  tools: string[];
}

const SCOPE_DEFINITIONS: ScopeDefinition[] = [
  {
    scope: 'trades',
    labelKey: 'mcpScopeTrades',
    tools: ['list_trades', 'get_trade', 'get_contract_status', 'get_position_summary'],
  },
  {
    scope: 'approvals',
    labelKey: 'mcpScopeApprovals',
    tools: ['get_workflow_history'],
  },
  {
    scope: 'valuation',
    labelKey: 'mcpScopeValuation',
    tools: [
      'get_valuations',
      'get_valuation_history',
      'get_valuation_inputs',
      'get_valuation_batches',
      'get_trade_valuation',
    ],
  },
  {
    scope: 'reference_data',
    labelKey: 'mcpScopeReferenceData',
    tools: [
      'get_reference_rate',
      'get_reference_rates_range',
      'get_holidays',
      'get_groupings_config',
    ],
  },
  {
    scope: 'expiry',
    labelKey: 'mcpScopeExpiry',
    tools: ['get_expiring_forwards', 'get_fixing_schedule'],
  },
  {
    scope: 'modifications',
    labelKey: 'mcpScopeModifications',
    tools: ['get_pending_modifications', 'get_modification_history'],
  },
  {
    scope: 'cashflows',
    labelKey: 'mcpScopeCashflows',
    tools: ['get_cashflows', 'get_cashflow_summary'],
  },
  {
    scope: 'confirmations',
    labelKey: 'mcpScopeConfirmations',
    tools: [
      'get_confirmation_status',
      'get_confirmation_details',
      'get_confirmation_emails',
    ],
  },
  {
    scope: 'accounting',
    labelKey: 'mcpScopeAccounting',
    tools: [
      'get_accounting_entries',
      'get_accounting_events',
      'get_accounting_rules',
      'get_accounting_batches',
    ],
  },
];

interface McpKeyModalProps {
  isOpen: boolean;
  clientId: string;
  userUid: string;
  userName: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onKeyChanged: () => void;
}

/**
 * Map backend error detail strings to i18n keys so the error banner
 * is displayed in the user's locale instead of raw English.
 */
const BACKEND_ERROR_MAP: Record<string, string> = {
  'User already has an active MCP key': 'mcpErrorAlreadyExists',
  'User does not have an MCP key': 'mcpErrorNoKey',
  'MCP key document not found': 'mcpErrorKeyNotFound',
  'Nothing to update': 'mcpErrorNothingToUpdate',
  'User not found': 'mcpErrorUserNotFound',
  'User does not belong to your organisation': 'mcpErrorForbidden',
};

const McpKeyModal: React.FC<McpKeyModalProps> = ({
  isOpen,
  clientId,
  userUid,
  userName,
  onClose,
  onSuccess,
  onError,
  onKeyChanged,
}) => {
  const { t } = useTranslation();

  /** Translate a backend error into the user's locale, with a fallback key. */
  const translateError = (raw: string, fallbackKey: string): string => {
    for (const [pattern, key] of Object.entries(BACKEND_ERROR_MAP)) {
      if (raw.includes(pattern)) {
        return t(`admin.users.${key}`);
      }
    }
    return t(`admin.users.${fallbackKey}`);
  };

  // Key metadata (null = no key exists)
  const [keyMeta, setKeyMeta] = useState<McpKeyMetadata | null>(null);
  const [loadingKey, setLoadingKey] = useState(false);

  // Scope selection
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [originalScopes, setOriginalScopes] = useState<string[]>([]);

  // Entity masking toggle
  const [maskingEnabled, setMaskingEnabled] = useState(true);
  const [originalMasking, setOriginalMasking] = useState(true);

  // One-time secret display
  const [secret, setSecret] = useState<string | null>(null);
  const [createdKeyId, setCreatedKeyId] = useState<string | null>(null);
  const [createdOauthClientId, setCreatedOauthClientId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null); // which field was copied
  const [showHelp, setShowHelp] = useState(false);

  // Loading states
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rotating, setRotating] = useState(false);

  // Confirm sub-dialogs
  const [confirmAction, setConfirmAction] = useState<'rotate' | 'delete' | null>(null);

  const isBusy = creating || saving || toggling || deleting || rotating;

  // --- Load key metadata ---
  // Always attempt to fetch from the backend so the modal is never stale.
  // If the user has no key the backend returns 404, handled gracefully below.
  const loadKeyMeta = useCallback(async () => {
    setLoadingKey(true);
    try {
      const response = await userService.getMcpKey(userUid);
      if (response.success && response.data) {
        setKeyMeta(response.data);
        setSelectedScopes([...response.data.scopes]);
        setOriginalScopes([...response.data.scopes]);
        setMaskingEnabled(response.data.entityMaskingEnabled ?? true);
        setOriginalMasking(response.data.entityMaskingEnabled ?? true);
      } else {
        // No key or key not found
        setKeyMeta(null);
        setSelectedScopes([]);
        setOriginalScopes([]);
        setMaskingEnabled(true);
        setOriginalMasking(true);
      }
    } catch {
      setKeyMeta(null);
      setSelectedScopes([]);
      setOriginalScopes([]);
      setMaskingEnabled(true);
      setOriginalMasking(true);
    } finally {
      setLoadingKey(false);
    }
  }, [userUid]);

  useEffect(() => {
    if (isOpen) {
      setSecret(null);
      setCopied(null);
      setConfirmAction(null);
      loadKeyMeta();
    }
  }, [isOpen, loadKeyMeta]);

  // --- Scope toggling ---
  const toggleScope = (scope: string) => {
    setSelectedScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  const scopesChanged = () => {
    if (selectedScopes.length !== originalScopes.length) return true;
    const sorted1 = [...selectedScopes].sort();
    const sorted2 = [...originalScopes].sort();
    return sorted1.some((s, i) => s !== sorted2[i]);
  };

  const settingsChanged = () => scopesChanged() || maskingEnabled !== originalMasking;

  // --- Create key ---
  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await userService.createMcpKey(userUid, selectedScopes);
      if (response.success && response.data) {
        setCreatedKeyId(response.data.keyId);
        setCreatedOauthClientId(response.data.oauthClientId || null);
        setSecret(response.data.keySecret);
        setCopied(null);
        onSuccess(t('admin.users.mcpCreateSuccess', { name: userName }));
        onKeyChanged();
        // Reload metadata to get the full key info (non-critical)
        try {
          const metaResponse = await userService.getMcpKey(userUid);
          if (metaResponse.success && metaResponse.data) {
            setKeyMeta(metaResponse.data);
            setOriginalScopes([...metaResponse.data.scopes]);
            setMaskingEnabled(metaResponse.data.entityMaskingEnabled ?? true);
            setOriginalMasking(metaResponse.data.entityMaskingEnabled ?? true);
          }
        } catch {
          // Metadata reload failed — not critical, key was created successfully
        }
      } else {
        onError(translateError(response.message || '', 'mcpErrorCreateFailed'));
      }
    } catch (err: any) {
      onError(translateError(err.message || '', 'mcpErrorCreateFailed'));
    } finally {
      setCreating(false);
    }
  };

  // --- Save scopes ---
  const handleSaveScopes = async () => {
    setSaving(true);
    try {
      const response = await userService.updateMcpKey(userUid, { scopes: selectedScopes, entityMaskingEnabled: maskingEnabled });
      if (response.success) {
        onSuccess(t('admin.users.mcpUpdateSuccess', { name: userName }));
        onKeyChanged();
        onClose();
      } else {
        onError(translateError(response.message || '', 'mcpErrorUpdateFailed'));
      }
    } catch (err: any) {
      onError(translateError(err.message || '', 'mcpErrorUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  // --- Toggle active/disabled ---
  const handleToggleActive = async () => {
    if (!keyMeta) return;
    setToggling(true);
    try {
      const newActive = !keyMeta.isActive;
      const response = await userService.updateMcpKey(userUid, { isActive: newActive });
      if (response.success) {
        onSuccess(t('admin.users.mcpUpdateSuccess', { name: userName }));
        onKeyChanged();
        onClose();
      } else {
        onError(translateError(response.message || '', 'mcpErrorUpdateFailed'));
      }
    } catch (err: any) {
      onError(translateError(err.message || '', 'mcpErrorUpdateFailed'));
    } finally {
      setToggling(false);
    }
  };

  // --- Delete key ---
  const handleDelete = async () => {
    setConfirmAction(null);
    setDeleting(true);
    try {
      const response = await userService.deleteMcpKey(userUid);
      if (response.success) {
        onSuccess(t('admin.users.mcpDeleteSuccess', { name: userName }));
        onKeyChanged();
        onClose();
      } else {
        onError(translateError(response.message || '', 'mcpErrorDeleteFailed'));
      }
    } catch (err: any) {
      onError(translateError(err.message || '', 'mcpErrorDeleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  // --- Rotate key ---
  const handleRotate = async () => {
    setConfirmAction(null);
    setRotating(true);
    try {
      const response = await userService.rotateMcpKey(userUid, selectedScopes);
      if (response.success && response.data) {
        setCreatedKeyId(response.data.keyId);
        setCreatedOauthClientId(response.data.oauthClientId || null);
        setSecret(response.data.keySecret);
        setCopied(null);
        onSuccess(t('admin.users.mcpRotateSuccess', { name: userName }));
        onKeyChanged();
        // Reload metadata (non-critical)
        try {
          const metaResponse = await userService.getMcpKey(userUid);
          if (metaResponse.success && metaResponse.data) {
            setKeyMeta(metaResponse.data);
            setOriginalScopes([...metaResponse.data.scopes]);
            setSelectedScopes([...metaResponse.data.scopes]);
            setMaskingEnabled(metaResponse.data.entityMaskingEnabled ?? true);
            setOriginalMasking(metaResponse.data.entityMaskingEnabled ?? true);
          }
        } catch {
          // Metadata reload failed — not critical, key was rotated successfully
        }
      } else {
        onError(translateError(response.message || '', 'mcpErrorRotateFailed'));
      }
    } catch (err: any) {
      onError(translateError(err.message || '', 'mcpErrorRotateFailed'));
    } finally {
      setRotating(false);
    }
  };

  // --- Copy a single credential value ---
  const handleCopyField = async (value: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(fieldName);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Fallback: ignore
    }
  };

  // --- Format date ---
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return t('admin.users.mcpNeverUsed');
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  // --- Close handler (block when secret is showing) ---
  const handleClose = () => {
    if (secret) return; // Must dismiss secret first
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !secret) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  // --- Render: One-time secret display ---
  if (secret) {
    const mcpUrl = (import.meta as any).env?.VITE_MCP_URL || 'http://127.0.0.1:8080/mcp';

    const credentials = [
      { label: 'MCP_URL', value: mcpUrl },
      { label: 'FXP_CLIENT_ID', value: clientId },
      { label: 'FXP_KEY_ID', value: createdKeyId || '' },
      { label: 'FXP_KEY_SECRET', value: secret, isSecret: true },
    ];

    // OAuth credentials for Copilot Studio / machine-to-machine clients
    let tokenUrl = '';
    try {
      tokenUrl = new URL('/token', mcpUrl).href;
    } catch {
      tokenUrl = mcpUrl.replace(/\/mcp$/, '/token');
    }

    let authUrl = '';
    let refreshUrl = '';
    try {
      authUrl = new URL('/authorize', mcpUrl).href;
      refreshUrl = tokenUrl; // refresh URL is the same as token URL
    } catch {
      authUrl = mcpUrl.replace(/\/mcp$/, '/authorize');
      refreshUrl = tokenUrl;
    }

    const oauthCredentials = createdOauthClientId ? [
      { label: t('admin.users.mcpOauthClientIdLabel'), value: createdOauthClientId },
      { label: t('admin.users.mcpOauthClientSecretLabel'), value: secret, isSecret: true },
      { label: t('admin.users.mcpOauthAuthUrlLabel'), value: authUrl },
      { label: t('admin.users.mcpOauthTokenUrlLabel'), value: tokenUrl },
      { label: t('admin.users.mcpOauthRefreshUrlLabel'), value: refreshUrl },
    ] : [];

    return (
      <div className="modal-overlay" onClick={() => {}}>
        <div className="modal-content mcp-modal mcp-modal-wide" onClick={e => e.stopPropagation()}>
          <h3>{t('admin.users.mcpModalTitle', { name: userName })}</h3>

          <div className="mcp-secret-display">
            <p className="mcp-connect-intro">{t('admin.users.mcpConnectIntro')}</p>

            {credentials.map(cred => (
              <div key={cred.label} className="mcp-credential-row">
                <span className="mcp-credential-label">{cred.label}</span>
                <code className={`mcp-credential-value${cred.isSecret ? ' mcp-secret-text' : ''}`}>
                  {cred.value}
                </code>
                <button
                  className="mcp-copy-icon"
                  onClick={() => handleCopyField(cred.value, cred.label)}
                  title={t('admin.users.mcpCopySecret')}
                >
                  {copied === cred.label ? '✓' : '⧉'}
                </button>
              </div>
            ))}

            {/* OAuth credentials for Copilot Studio */}
            {oauthCredentials.length > 0 && (
              <>
                <div className="mcp-section-divider" />
                <p className="mcp-connect-intro">{t('admin.users.mcpOauthIntro')}</p>

                {oauthCredentials.map(cred => (
                  <div key={cred.label} className="mcp-credential-row">
                    <span className="mcp-credential-label">{cred.label}</span>
                    <code className={`mcp-credential-value${cred.isSecret ? ' mcp-secret-text' : ''}`}>
                      {cred.value}
                    </code>
                    <button
                      className="mcp-copy-icon"
                      onClick={() => handleCopyField(cred.value, `oauth-${cred.label}`)}
                      title={t('admin.users.mcpCopySecret')}
                    >
                      {copied === `oauth-${cred.label}` ? '✓' : '⧉'}
                    </button>
                  </div>
                ))}
              </>
            )}

            <p className="mcp-secret-warning">{t('admin.users.mcpSecretWarning')}</p>

            {/* "Cómo conectar" help toggle — commented out; all URLs now shown directly above
            <button
              className="mcp-help-toggle"
              onClick={() => setShowHelp(!showHelp)}
            >
              {showHelp ? '▾' : '▸'} {t('admin.users.mcpHelpToggle')}
            </button>

            {showHelp && (
              <div className="mcp-help-text">
                <p>{t('admin.users.mcpHelpStep1')}</p>
                <p>{t('admin.users.mcpHelpStep2')}</p>
                <p>{t('admin.users.mcpHelpStep3')}</p>
              </div>
            )}
            */}
          </div>

          <div className="modal-actions">
            <button
              className="primary-button"
              onClick={() => {
                setSecret(null);
                setCreatedKeyId(null);
                setCreatedOauthClientId(null);
                setCopied(null);
                setShowHelp(false);
                setKeyMeta(null);
                onClose();
              }}
            >
              {t('admin.users.mcpSecretSaved')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Confirm sub-dialog ---
  if (confirmAction) {
    return (
      <div className="modal-overlay" onClick={() => setConfirmAction(null)}>
        <div className="modal-content mcp-modal" onClick={e => e.stopPropagation()}>
          <h3>
            {confirmAction === 'rotate'
              ? t('admin.users.mcpRotateKey')
              : t('admin.users.mcpDeleteKey')}
          </h3>
          <p className="mcp-confirm-message">
            {confirmAction === 'rotate'
              ? t('admin.users.mcpConfirmRotate')
              : t('admin.users.mcpConfirmDelete')}
          </p>
          <div className="modal-actions">
            <button className="secondary-button" onClick={() => setConfirmAction(null)}>
              {t('common.cancel')}
            </button>
            <button
              className="primary-button danger-button"
              onClick={confirmAction === 'rotate' ? handleRotate : handleDelete}
            >
              {confirmAction === 'rotate'
                ? t('admin.users.mcpRotateKey')
                : t('admin.users.mcpDeleteKey')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Loading ---
  if (loadingKey) {
    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal-content mcp-modal" onClick={e => e.stopPropagation()}>
          <h3>{t('admin.users.mcpModalTitle', { name: userName })}</h3>
          <div className="mcp-loading">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Main modal ---
  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content mcp-modal" onClick={e => e.stopPropagation()}>
        <div className="mcp-modal-header">
          <h3>{t('admin.users.mcpModalTitle', { name: userName })}</h3>
          {keyMeta && (
            <span className={`status-badge ${keyMeta.isActive ? 'status-active' : 'status-inactive'}`}>
              {keyMeta.isActive ? t('admin.users.mcpActive') : t('admin.users.mcpDisabled')}
            </span>
          )}
        </div>

        {/* Key metadata (only when key exists) */}
        {keyMeta && (
          <div className="mcp-key-meta">
            <span>{t('admin.users.mcpKeyIdLabel')}: {keyMeta.keyId}</span>
            <span className="mcp-meta-separator">·</span>
            <span>{t('admin.users.mcpCreated')}: {formatDate(keyMeta.createdAt)}</span>
            <span className="mcp-meta-separator">·</span>
            <span>{t('admin.users.mcpLastUsed')}: {formatDate(keyMeta.lastUsedAt)}</span>
            <span className="mcp-meta-separator">·</span>
            <span>{t('admin.users.mcpAgentCalls')}: {keyMeta.callCount}</span>
            {keyMeta.oauthClientId && (
              <>
                <span className="mcp-meta-separator">·</span>
                <span>{t('admin.users.mcpOauthClientIdShort')}: {keyMeta.oauthClientId}</span>
              </>
            )}
          </div>
        )}

        {/* Scope selector */}
        <div className="mcp-scopes-section">
          {SCOPE_DEFINITIONS.map(def => (
            <div key={def.scope} className="mcp-scope-group">
              <label className="mcp-scope-checkbox">
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(def.scope)}
                  onChange={() => toggleScope(def.scope)}
                  disabled={isBusy}
                />
                <span className="mcp-scope-name">{t(`admin.users.${def.labelKey}`)}</span>
              </label>
              <div className="mcp-scope-tools">
                {def.tools.map(tool => (
                  <span key={tool} className="mcp-tool-name">{tool}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Entity masking toggle */}
        <div className="mcp-masking-section">
          <label className="mcp-masking-toggle">
            <input
              type="checkbox"
              checked={maskingEnabled}
              onChange={() => setMaskingEnabled(!maskingEnabled)}
              disabled={isBusy}
            />
            <span className="mcp-masking-label">{t('admin.users.mcpEntityMasking', 'Entity Masking')}</span>
          </label>
          <p className="mcp-masking-hint">
            {t('admin.users.mcpEntityMaskingHint', 'When enabled, counterparty and organisation names are replaced with aliases in AI tool responses. Configure aliases in the Entity Aliases tab.')}
          </p>
        </div>

        {/* Actions */}
        <div className="mcp-modal-actions">
          {keyMeta ? (
            <>
              <div className="mcp-actions-right">
                <button className="secondary-button" onClick={handleClose} disabled={isBusy}>
                  {t('common.cancel')}
                </button>
                <button
                  className="primary-button"
                  onClick={handleSaveScopes}
                  disabled={!settingsChanged() || isBusy}
                >
                  {saving ? t('admin.users.saving') : t('admin.users.mcpSaveScopes')}
                </button>
              </div>
              <div className="mcp-actions-left">
                <button
                  className={`action-btn ${keyMeta.isActive ? 'danger' : 'save'}`}
                  onClick={handleToggleActive}
                  disabled={isBusy}
                  title={keyMeta.isActive
                    ? t('admin.users.mcpTooltipDisable')
                    : t('admin.users.mcpTooltipEnable')}
                >
                  {keyMeta.isActive
                    ? t('admin.users.mcpDisable')
                    : t('admin.users.mcpEnable')}
                </button>
                <button
                  className="action-btn"
                  onClick={() => setConfirmAction('rotate')}
                  disabled={isBusy}
                  title={t('admin.users.mcpTooltipRotate')}
                >
                  {t('admin.users.mcpRotateKey')}
                </button>
                <button
                  className="action-btn danger"
                  onClick={() => setConfirmAction('delete')}
                  disabled={isBusy}
                  title={t('admin.users.mcpTooltipRevoke')}
                >
                  {t('admin.users.mcpDeleteKey')}
                </button>
              </div>
            </>
          ) : (
            <div className="mcp-actions-right">
              <button className="secondary-button" onClick={handleClose}>
                {t('common.cancel')}
              </button>
              <button
                className="primary-button"
                onClick={handleCreate}
                disabled={creating || selectedScopes.length === 0}
              >
                {creating ? t('common.loading') : t('admin.users.mcpCreateKey')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default McpKeyModal;
